import React, { useState, useEffect, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  getCalendar,
  getUsersSummary,
  getAllGroups,
  getUserById,
} from "../../../api/api";
import {
  getLeaveTypes,
  addHrInspectorReport,
  addHrInspectorTravelReport,
  getHrInspectorReports,
  deleteHrInspectorReport,
} from "../../../api/reportsApi";
import "./VacationManagement.css";

registerLocale("ru", ru);

const VacationManagement = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDates, setSelectedDates] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [calendarData, setCalendarData] = useState({});
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isLoadingLeaveTypes, setIsLoadingLeaveTypes] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingDeleteReportIds, setPendingDeleteReportIds] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  const subTabs = [
    { id: "create", label: "СОЗДАТЬ" },
    { id: "edit", label: "РЕДАКТИРОВАТЬ" },
    { id: "all", label: "ВСЕ" },
  ];

  // Сброс значений при смене вкладки
  useEffect(() => {
    setSelectedDepartment(null);
    setSelectedEmployee(null);
    setSelectedDates({ startDate: null, endDate: null });
    setSelectedLeaveType(null);
    setSelectedProject(null);
    setReports([]);
  }, [activeTab]);

  // Загрузка типов отпусков
  useEffect(() => {
    const loadLeaveTypes = async () => {
      if (!selectedDepartment || !selectedEmployee) {
        setLeaveTypes([]);
        return;
      }

      try {
        setIsLoadingLeaveTypes(true);
        const types = await getLeaveTypes({
          group_id: selectedDepartment.value,
          user_id: selectedEmployee.value,
        });
        setLeaveTypes(types);
      } catch (error) {
        console.error("Ошибка при загрузке типов отпусков:", error);
      } finally {
        setIsLoadingLeaveTypes(false);
      }
    };

    loadLeaveTypes();
  }, [selectedDepartment, selectedEmployee]);

  // Загрузка списка отделов
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setIsLoadingDepartments(true);
        const allGroups = await getAllGroups();
        const uniqueDepartments = allGroups
          .filter((group) => group.is_active)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }));
        setDepartments(uniqueDepartments);
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  // Загрузка списка сотрудников
  useEffect(() => {
    const loadEmployees = async () => {
      if (!selectedDepartment) {
        setEmployees([]);
        return;
      }

      try {
        setIsLoadingEmployees(true);
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 1000,
          groupId: selectedDepartment.value,
        });

        const formattedEmployees = response.users.map((user) => ({
          value: user.id,
          label: `${user.surname} ${user.name} ${user.patronymic}`.trim(),
        }));

        setEmployees(formattedEmployees);
        setSelectedEmployee(null); // Сбрасываем выбранного сотрудника при смене отдела
      } catch (error) {
        console.error("Ошибка при загрузке списка сотрудников:", error);
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [selectedDepartment]);

  // Загрузка данных календаря
  const loadCalendarData = useCallback(async () => {
    try {
      const currentDate = new Date();
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 12,
        0
      );

      const dateFrom = firstDay.toISOString().split("T")[0];
      const dateTo = lastDay.toISOString().split("T")[0];

      const data = await getCalendar(dateFrom, dateTo);
      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // Загрузка проектов пользователя
  useEffect(() => {
    const loadProjects = async () => {
      if (!selectedEmployee || selectedLeaveType?.label !== "Командировка") {
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const response = await getUserById(selectedEmployee.value);

        // Проверяем структуру ответа
        console.log("Ответ API getUserById:", response);

        let userData;
        if (response && response.users && response.users.length > 0) {
          userData = response.users[0];
        } else if (response && response.id) {
          // Если API возвращает пользователя напрямую
          userData = response;
        } else {
          console.warn("Неожиданная структура ответа API:", response);
          setProjects([]);
          return;
        }

        if (userData && userData.projects && userData.projects.length > 0) {
          const userProjects = userData.projects.map((project) => ({
            value: project.id,
            label: project.cipher_pilot,
          }));
          setProjects(userProjects);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error("Ошибка при загрузке проектов:", error);
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [selectedEmployee, selectedLeaveType]);

  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    if (!dayData) return "";

    const classes = [];

    // Добавляем класс для выходных всегда
    if (!dayData.workday_flag) {
      classes.push("highlight-weekend");
    }

    // Добавляем класс для событий
    if (dayData.event_name) {
      classes.push("has-event");
    }

    return classes.join(" ");
  };

  const filterDate = (date) => {
    // Убираем ограничение на выходные дни - разрешаем выбирать любые дни
    return true;
  };

  const handleDateChange = (date, type) => {
    if (type === "start") {
      if (selectedDates.endDate && date > selectedDates.endDate) {
        setSelectedDates({ startDate: date, endDate: null });
      } else {
        setSelectedDates((prev) => ({ ...prev, startDate: date }));
      }
    } else {
      setSelectedDates((prev) => ({ ...prev, endDate: date }));
    }
  };

  const getWorkingDaysInRange = (startDate, endDate) => {
    const workingDays = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);

    while (currentDate <= endDateTime) {
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      if (dayData?.workday_flag) {
        workingDays.push(dateString);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  const handleSubmit = async () => {
    if (
      !selectedDepartment ||
      !selectedEmployee ||
      !selectedDates.startDate ||
      !selectedDates.endDate ||
      !selectedLeaveType ||
      (selectedLeaveType.label === "Командировка" && !selectedProject)
    ) {
      alert("Пожалуйста, заполните все поля");
      return;
    }

    try {
      setIsSubmitting(true);
      const startDate = new Date(selectedDates.startDate);
      const endDate = new Date(selectedDates.endDate);

      // Для командировок используем только рабочие дни
      let period;
      if (selectedLeaveType.label === "Командировка") {
        period = getWorkingDaysInRange(startDate, endDate);

        // Проверяем, что есть рабочие дни в выбранном периоде
        if (period.length === 0) {
          alert("В выбранном периоде нет рабочих дней");
          return;
        }
      } else {
        // Для других типов отпусков используем все дни
        period = [];
        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          period.push(currentDate.toLocaleDateString("en-CA"));
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      const reportData = {
        user_id: selectedEmployee.value,
        group_id: selectedDepartment.value,
        period: period,
        portal_job_type_id: selectedLeaveType.value,
        ...(selectedLeaveType.label === "Командировка" && {
          project_id: selectedProject.value,
        }),
      };

      console.log("Подготовленные данные для отправки:", reportData);

      if (selectedLeaveType.label === "Командировка") {
        await addHrInspectorTravelReport(reportData);
      } else {
        await addHrInspectorReport(reportData);
      }

      setError(null);
      setShowPopup(true);

      // Сбрасываем форму через 3 секунды после успешного создания
      setTimeout(() => {
        setShowPopup(false);
        setSelectedEmployee(null);
        setSelectedDates({ startDate: null, endDate: null });
        setSelectedLeaveType(null);
        setSelectedProject(null);
        setSelectedDepartment(null);
      }, 3000);
    } catch (error) {
      setError(`Ошибка при создании отчета: ${error.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Загрузка отчетов для выбранного сотрудника
  useEffect(() => {
    const loadReports = async () => {
      if (!selectedEmployee || !selectedDepartment || activeTab !== "edit") {
        console.log("Пропускаем загрузку отчетов:", {
          selectedEmployee,
          selectedDepartment,
          activeTab,
        });
        setReports([]);
        return;
      }

      try {
        setIsLoadingReports(true);
        console.log("Начинаем загрузку отчетов с параметрами:", {
          user_id: selectedEmployee.value,
          group_id: selectedDepartment.value,
        });

        const response = await getHrInspectorReports({
          user_id: selectedEmployee.value,
          group_id: selectedDepartment.value,
          page_size: 1000,
        });

        console.log("Получены отчеты:", response);

        // Сначала сортируем отчеты по дате
        const sortedReports = [...response.reports].sort(
          (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start)
        );

        // Группировка отчетов по типам и периодам
        const groupedReports = {};
        let currentGroup = null;

        sortedReports.forEach((report) => {
          console.log("Обрабатываем отчет:", report);

          if (!report.fact_date_start || !report.portal_job_type_name) {
            console.warn(
              "Пропускаем отчет из-за отсутствия необходимых полей:",
              report
            );
            return;
          }

          const currentDate = new Date(report.fact_date_start);

          // Если это первый отчет или новый тип отпуска, создаем новую группу
          if (
            !currentGroup ||
            currentGroup.type !== report.portal_job_type_name ||
            Math.abs(currentDate - new Date(currentGroup.endDate)) >
              24 * 60 * 60 * 1000
          ) {
            // Если была предыдущая группа, сохраняем ее
            if (currentGroup) {
              const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}`;
              groupedReports[key] = currentGroup;
            }

            // Создаем новую группу
            currentGroup = {
              type: report.portal_job_type_name,
              startDate: report.fact_date_start,
              endDate: report.fact_date_start,
              reportIds: [report.id],
              leaveType: {
                label: report.portal_job_type_name,
              },
            };
          } else {
            // Продолжаем текущую группу
            currentGroup.endDate = report.fact_date_start;
            currentGroup.reportIds.push(report.id);
          }
        });

        // Не забываем добавить последнюю группу
        if (currentGroup) {
          const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}`;
          groupedReports[key] = currentGroup;
        }

        console.log("Сгруппированные отчеты:", groupedReports);
        setReports(Object.values(groupedReports));
      } catch (error) {
        console.error("Ошибка при загрузке отчетов:", error);
        setError("Ошибка при загрузке отчетов");
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadReports();
  }, [selectedEmployee, selectedDepartment, activeTab]);

  // Загрузка отчетов для всего отдела
  useEffect(() => {
    const loadAllReports = async () => {
      if (!selectedDepartment || activeTab !== "all") {
        return;
      }

      try {
        setIsLoadingReports(true);
        console.log("Начинаем загрузку всех отчетов отдела:", {
          group_id: selectedDepartment.value,
        });

        const response = await getHrInspectorReports({
          group_id: selectedDepartment.value,
          page_size: 1000,
        });

        console.log("Получены отчеты отдела:", response);

        // Сначала сортируем отчеты по дате
        const sortedReports = [...response.reports].sort(
          (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start)
        );

        // Группировка отчетов по типам и периодам
        const groupedReports = {};
        let currentGroup = null;

        sortedReports.forEach((report) => {
          if (!report.fact_date_start || !report.portal_job_type_name) {
            console.warn(
              "Пропускаем отчет из-за отсутствия необходимых полей:",
              report
            );
            return;
          }

          const currentDate = new Date(report.fact_date_start);
          const employeeName = `${report.user}`.trim();

          // Если это первый отчет или новый тип отпуска или новый сотрудник, создаем новую группу
          if (
            !currentGroup ||
            currentGroup.type !== report.portal_job_type_name ||
            currentGroup.employeeName !== employeeName ||
            Math.abs(currentDate - new Date(currentGroup.endDate)) >
              24 * 60 * 60 * 1000
          ) {
            // Если была предыдущая группа, сохраняем ее
            if (currentGroup) {
              const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}_${currentGroup.employeeName}`;
              groupedReports[key] = currentGroup;
            }

            // Создаем новую группу
            currentGroup = {
              type: report.portal_job_type_name,
              startDate: report.fact_date_start,
              endDate: report.fact_date_start,
              employeeName: employeeName,
              reportIds: [report.id],
              leaveType: {
                label: report.portal_job_type_name,
              },
            };
          } else {
            // Продолжаем текущую группу
            currentGroup.endDate = report.fact_date_start;
            currentGroup.reportIds.push(report.id);
          }
        });

        // Не забываем добавить последнюю группу
        if (currentGroup) {
          const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}_${currentGroup.employeeName}`;
          groupedReports[key] = currentGroup;
        }

        console.log("Сгруппированные отчеты отдела:", groupedReports);
        setReports(Object.values(groupedReports));
      } catch (error) {
        console.error("Ошибка при загрузке отчетов отдела:", error);
        setError("Ошибка при загрузке отчетов");
      } finally {
        setIsLoadingReports(false);
      }
    };

    if (activeTab === "all") {
      loadAllReports();
    }
  }, [selectedDepartment, activeTab]);

  const handleDeleteReport = async (reportIds) => {
    setPendingDeleteReportIds(reportIds);
    setShowConfirmPopup(true);
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      setShowConfirmPopup(false);

      // Последовательно удаляем все отчеты в периоде
      for (const reportId of pendingDeleteReportIds) {
        await deleteHrInspectorReport(reportId);
      }

      // Обновляем список отчетов после удаления
      setReports(
        reports.filter(
          (report) =>
            !report.reportIds.some((id) => pendingDeleteReportIds.includes(id))
        )
      );

      setError(null);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } catch (error) {
      setError(`Ошибка при удалении отчета: ${error.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
      setPendingDeleteReportIds(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmPopup(false);
    setPendingDeleteReportIds(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "create":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Отдел</label>
              <Select
                value={selectedDepartment}
                onChange={(option) => {
                  setSelectedDepartment(option);
                }}
                options={departments}
                className="department-select"
                placeholder="Выберите отдел"
                isLoading={isLoadingDepartments}
                isDisabled={isLoadingDepartments}
              />
            </div>

            <div className="form-group">
              <label>Сотрудник</label>
              <Select
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                options={employees}
                className="employee-select"
                placeholder="Выберите сотрудника"
                isLoading={isLoadingEmployees}
                isDisabled={isLoadingEmployees || !selectedDepartment}
              />
            </div>

            <div className="form-group">
              <label>Период</label>
              {selectedLeaveType?.label === "Командировка" && (
                <div className="form-hint">
                  Для командировок записи создаются только на рабочие дни
                </div>
              )}
              <div className="date-range-picker">
                <DatePicker
                  selected={selectedDates.startDate}
                  onChange={(date) => handleDateChange(date, "start")}
                  selectsStart
                  startDate={selectedDates.startDate}
                  endDate={selectedDates.endDate}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Начало"
                  locale="ru"
                  calendarStartDay={1}
                  dayClassName={highlightDays}
                  filterDate={filterDate}
                  calendarClassName="custom-calendar"
                  renderDayContents={(day, date) => {
                    const dateString = date.toLocaleDateString("en-CA");
                    const dayData = calendarData[dateString];
                    return (
                      <div
                        className="day-content"
                        data-tooltip={dayData?.event_name || ""}
                      >
                        {day}
                      </div>
                    );
                  }}
                  className="date-picker"
                />
                <span className="date-range-separator">—</span>
                <DatePicker
                  selected={selectedDates.endDate}
                  onChange={(date) => handleDateChange(date, "end")}
                  selectsEnd
                  startDate={selectedDates.startDate}
                  endDate={selectedDates.endDate}
                  minDate={selectedDates.startDate}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Конец"
                  locale="ru"
                  calendarStartDay={1}
                  dayClassName={highlightDays}
                  filterDate={filterDate}
                  calendarClassName="custom-calendar"
                  renderDayContents={(day, date) => {
                    const dateString = date.toLocaleDateString("en-CA");
                    const dayData = calendarData[dateString];
                    return (
                      <div
                        className="day-content"
                        data-tooltip={dayData?.event_name || ""}
                      >
                        {day}
                      </div>
                    );
                  }}
                  className="date-picker"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Тип</label>
              <Select
                value={selectedLeaveType}
                onChange={setSelectedLeaveType}
                options={leaveTypes}
                className="leave-type-select"
                placeholder="Выберите тип"
                isLoading={isLoadingLeaveTypes}
                isDisabled={
                  isLoadingLeaveTypes ||
                  !selectedDepartment ||
                  !selectedEmployee
                }
              />
            </div>

            {selectedLeaveType?.label === "Командировка" && (
              <div className="form-group">
                <label>Проект</label>
                <Select
                  value={selectedProject}
                  onChange={setSelectedProject}
                  options={projects}
                  className="project-select"
                  placeholder="Выберите проект"
                  isLoading={isLoadingProjects}
                  isDisabled={isLoadingProjects}
                />
              </div>
            )}

            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={
                !selectedDepartment ||
                !selectedEmployee ||
                !selectedDates.startDate ||
                !selectedDates.endDate ||
                !selectedLeaveType ||
                (selectedLeaveType?.label === "Командировка" &&
                  !selectedProject) ||
                isSubmitting
              }
            >
              Сохранить
            </button>
          </div>
        );
      case "edit":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Отдел</label>
              <Select
                value={selectedDepartment}
                onChange={(option) => {
                  setSelectedDepartment(option);
                }}
                options={departments}
                className="department-select"
                placeholder="Выберите отдел"
                isLoading={isLoadingDepartments}
                isDisabled={isLoadingDepartments}
              />
            </div>

            <div className="form-group">
              <label>Сотрудник</label>
              <Select
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                options={employees}
                className="employee-select"
                placeholder="Выберите сотрудника"
                isLoading={isLoadingEmployees}
                isDisabled={isLoadingEmployees || !selectedDepartment}
              />
            </div>

            {selectedEmployee && selectedDepartment && (
              <div className="reports-list">
                {isLoadingReports ? (
                  <div className="loading">Загрузка отчетов...</div>
                ) : reports.length > 0 ? (
                  <div className="reports-table">
                    <div className="reports-table__header">
                      <div className="reports-table__cell">Период</div>
                      <div className="reports-table__cell">Тип</div>
                      <div className="reports-table__cell">Действия</div>
                    </div>
                    {reports.map((report) => (
                      <div
                        key={`${report.startDate}_${report.endDate}_${report.type}`}
                        className="reports-table__row"
                      >
                        <div className="reports-table__cell">
                          {new Date(report.endDate).toLocaleDateString("ru-RU")}
                          {report.startDate !== report.endDate && (
                            <>
                              {" — "}
                              {new Date(report.startDate).toLocaleDateString(
                                "ru-RU"
                              )}
                            </>
                          )}
                        </div>
                        <div className="reports-table__cell">
                          {report.leaveType?.label || "Неизвестный тип"}
                        </div>
                        <div className="reports-table__cell">
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteReport(report.reportIds)}
                            disabled={isSubmitting}
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-reports">Нет отчетов для отображения</div>
                )}
              </div>
            )}
          </div>
        );
      case "all":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Отдел</label>
              <Select
                value={selectedDepartment}
                onChange={(option) => {
                  setSelectedDepartment(option);
                }}
                options={departments}
                className="department-select"
                placeholder="Выберите отдел"
                isLoading={isLoadingDepartments}
                isDisabled={isLoadingDepartments}
              />
            </div>

            {selectedDepartment && (
              <div className="reports-list">
                {isLoadingReports ? (
                  <div className="loading">Загрузка отчетов...</div>
                ) : reports.length > 0 ? (
                  <div className="reports-table">
                    <div className="reports-table__header">
                      <div className="reports-table__cell">Период</div>
                      <div className="reports-table__cell">Сотрудник</div>
                      <div className="reports-table__cell">Тип</div>
                    </div>
                    {reports.map((report) => (
                      <div
                        key={`${report.startDate}_${report.endDate}_${report.type}_${report.employeeName}`}
                        className="reports-table__row"
                      >
                        <div className="reports-table__cell">
                          {new Date(report.endDate).toLocaleDateString("ru-RU")}
                          {report.startDate !== report.endDate && (
                            <>
                              {" — "}
                              {new Date(report.startDate).toLocaleDateString(
                                "ru-RU"
                              )}
                            </>
                          )}
                        </div>
                        <div className="reports-table__cell">
                          {report.employeeName}
                        </div>
                        <div className="reports-table__cell">
                          {report.leaveType?.label || "Неизвестный тип"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-reports">Нет отчетов для отображения</div>
                )}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`vacation-management ${isSubmitting ? "submitting" : ""}`}>
      <h2 className="vacation-management__title">
        Управление отпусками и больничными
      </h2>

      <div className="sub-navigation">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={
              tab.id === activeTab
                ? "sub-navigation__button sub-navigation__button-active"
                : "sub-navigation__button"
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {renderContent()}

      {showConfirmPopup && (
        <div className="popup-overlay">
          <div className="popup popup-confirm">
            <p className="popup-text">
              Вы уверены, что хотите удалить этот период?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={cancelDelete}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={confirmDelete}
                disabled={isSubmitting}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay">
          <div className={`popup ${error ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${error ? "popup-icon-error" : ""}`}
            ></div>
            <p className="popup-text">
              {error ||
                (activeTab === "create"
                  ? "Отчет успешно создан"
                  : "Отчет успешно удален")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacationManagement;
